import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { I18nProvider } from "@/i18n/I18nContext";
import { AuthProvider, useAuth } from "@/auth/AuthContext";
import { MultiProfileProvider } from "@/auth/MultiProfileContext";
import { RegisterModalProvider } from "@/components/RegisterModal";
import useAnalytics from "@/hooks/useAnalytics";
import { PageShell, Logo } from "@/components/Layout";
import Home from "@/pages/Home";
import Directory from "@/pages/Directory";
import ProfileDetail from "@/pages/ProfileDetail";
import Login from "@/pages/Login";
import RegisterAccount from "@/pages/RegisterAccount";
import Dashboard from "@/pages/Dashboard";
import EntityDashboard from "@/pages/EntityDashboard";
import ClientDashboard from "@/pages/ClientDashboard";
import MyProfiles from "@/pages/MyProfiles";
import ProfileForm from "@/pages/ProfileForm";
import ProfileCatalogPage from "@/pages/ProfileCatalogPage";
import ProfileAgendaPage from "@/pages/ProfileAgendaPage";
import PlansPage from "@/pages/PlansPage";
import AdminPanel from "@/pages/AdminPanel";
import Events from "@/pages/Events";
import RedApoyo from "@/pages/RedApoyo";
import MyBookings from "@/pages/MyBookings";
import About from "@/pages/About";
import CommunityLanding from "@/pages/CommunityLanding";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PublicStats from "@/pages/PublicStats";

const Stand = ({ children }) => <PageShell>{children}</PageShell>;

// Minimal, distraction-free shell for the QR inscription flow: only a centered
// logo on top and the form below — no site navbar/footer. Opening the QR shows
// the registration form immediately.
const FocusShell = ({ children }) => (
  <div className="min-h-screen bg-cream flex flex-col">
    <header className="py-5 flex justify-center border-b border-black/5 bg-white/70 backdrop-blur-sm">
      <a href="/" aria-label="Inicio"><Logo className="h-12" /></a>
    </header>
    <main className="flex-1">{children}</main>
  </div>
);

const AnalyticsTracker = () => { useAnalytics(); return null; };

const SmartDashboard = () => {
  const { user } = useAuth();
  if (user === null) return <div className="container-tight py-24 text-center text-teal-soft">…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  if (user.role === "entrepreneur" || user.role === "professional") return <Dashboard />;
  if (user.role === "entity") return <EntityDashboard />;
  return <ClientDashboard />;
};

const ContactRedirect = () => { React.useEffect(() => { window.location.href = "/#contact"; }, []); return null; };

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <MultiProfileProvider>
          <BrowserRouter>
            <RegisterModalProvider>
            <AnalyticsTracker />
            <Routes>
            <Route path="/" element={<Stand><Home /></Stand>} />
            <Route path="/directory" element={<Stand><Directory /></Stand>} />
            <Route path="/eventos" element={<Stand><Events /></Stand>} />
            <Route path="/events" element={<Stand><Events /></Stand>} />
            <Route path="/red-apoyo" element={<Stand><RedApoyo /></Stand>} />
            <Route path="/red-online" element={<Navigate to="/red-apoyo" replace />} />
            <Route path="/red-apoyo/mis-citas" element={<Stand><MyBookings /></Stand>} />
            <Route path="/red-online/mis-citas" element={<Navigate to="/red-apoyo/mis-citas" replace />} />
            <Route path="/conocenos" element={<Stand><About /></Stand>} />
            <Route path="/about" element={<Stand><About /></Stand>} />
            <Route path="/entrepreneur/:id" element={<Stand><ProfileDetail /></Stand>} />
            <Route path="/user" element={<Stand><CommunityLanding /></Stand>} />
            <Route path="/comunidad" element={<Navigate to="/user" replace />} />
            <Route path="/login" element={<Stand><Login /></Stand>} />
            {/* Legacy /register/* URLs → redirect to unified /register/account with the right kind */}
            <Route path="/register" element={<Navigate to="/register/account" replace />} />
            <Route path="/register/account" element={<Stand><RegisterAccount /></Stand>} />
            {/* Focused inscription form for QR codes — no site chrome */}
            <Route path="/inscribirse" element={<FocusShell><RegisterAccount /></FocusShell>} />
            <Route path="/inscripcion" element={<Navigate to="/inscribirse" replace />} />
            <Route path="/register/client" element={<Navigate to="/register/account" replace />} />
            <Route path="/register/business" element={<Navigate to="/register/account?kind=business" replace />} />
            <Route path="/register/professional" element={<Navigate to="/register/account?kind=professional" replace />} />
            <Route path="/register/entity" element={<Navigate to="/register/account?kind=ngo" replace />} />
            <Route path="/forgot-password" element={<Stand><ForgotPassword /></Stand>} />
            <Route path="/reset-password" element={<Stand><ResetPassword /></Stand>} />
            <Route path="/dashboard" element={<Stand><SmartDashboard /></Stand>} />
            <Route path="/my-profiles" element={<Stand><MyProfiles /></Stand>} />
            <Route path="/profiles/new" element={<Stand><ProfileForm mode="create" /></Stand>} />
            <Route path="/profiles/edit/:id" element={<Stand><ProfileForm mode="edit" /></Stand>} />
            <Route path="/profiles/:id/catalog" element={<Stand><ProfileCatalogPage /></Stand>} />
            <Route path="/profiles/:id/agenda" element={<Stand><ProfileAgendaPage /></Stand>} />
            <Route path="/plans" element={<Stand><PlansPage /></Stand>} />
            <Route path="/entity" element={<Stand><EntityDashboard /></Stand>} />
            <Route path="/admin" element={<Stand><AdminPanel /></Stand>} />
            <Route path="/estadisticas" element={<PublicStats />} />
            <Route path="/stats" element={<PublicStats />} />
            <Route path="/contact" element={<Stand><ContactRedirect /></Stand>} />
          </Routes>
          </RegisterModalProvider>
        </BrowserRouter>
        </MultiProfileProvider>
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
